<template>
  <div class="dark-theme-wrapper">
    <div class="container py-5">
      <h1 class="main-title text-center mb-5">SPRÁVA OTÁZEK</h1>

      <div class="admin-card mx-auto">
        <h2 class="section-title text-center mb-4">PŘIDAT OTÁZKU</h2>

        <form @submit.prevent="submitQuestion">
          <div class="row">
            <div class="col-md-6 mb-4">
              <label class="form-label">ČÍSLO KOLA</label>
              <div class="input-group">
                <span class="input-icon-wrapper">
                  <svg
                    class="icon-sm"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  >
                    <line x1="4" y1="9" x2="20" y2="9"></line>
                    <line x1="4" y1="15" x2="20" y2="15"></line>
                    <line x1="10" y1="3" x2="8" y2="21"></line>
                    <line x1="16" y1="3" x2="14" y2="21"></line>
                  </svg>
                </span>
                <input
                  type="number"
                  v-model.number="roundNumber"
                  class="form-control dark-input"
                  placeholder="Např. 1"
                  min="1"
                  required
                />
              </div>
            </div>

            <div class="col-md-6 mb-4">
              <label class="form-label">DATUM A ČAS UZÁVĚRKY</label>
              <div class="input-group">
                <span class="input-icon-wrapper">
                  <svg
                    class="icon-sm"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  >
                    <circle cx="12" cy="12" r="10"></circle>
                    <polyline points="12 6 12 12 16 14"></polyline>
                  </svg>
                </span>
                <input
                  type="datetime-local"
                  v-model="deadline"
                  class="form-control dark-input"
                  required
                />
              </div>
            </div>
          </div>

          <div class="mb-5">
            <label class="form-label">ZNĚNÍ OTÁZKY</label>
            <div class="input-group">
              <span class="input-icon-wrapper">
                <svg
                  class="icon-sm"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                >
                  <circle cx="12" cy="12" r="10"></circle>
                  <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"></path>
                  <line x1="12" y1="17" x2="12.01" y2="17"></line>
                </svg>
              </span>
              <input
                type="text"
                v-model="questionText"
                class="form-control dark-input"
                placeholder="Např. Padne v tomto kole červená karta?"
                required
              />
            </div>
          </div>

          <button type="submit" class="btn-red-glow w-100" :disabled="loading">
            <span
              v-if="loading"
              class="spinner-border spinner-border-sm me-2"
              role="status"
            ></span>
            {{ loading ? "UKLÁDÁM..." : "ULOŽIT OTÁZKU" }}
          </button>
        </form>

        <transition name="fade">
          <div v-if="successMessage" class="alert-box success mt-4">
            <svg
              class="icon-sm me-2"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
              <polyline points="22 4 12 14.01 9 11.01"></polyline>
            </svg>
            {{ successMessage }}
          </div>
        </transition>

        <transition name="fade">
          <div v-if="errorMessage" class="alert-box error mt-4">
            <svg
              class="icon-sm me-2"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <circle cx="12" cy="12" r="10"></circle>
              <line x1="12" y1="8" x2="12" y2="12"></line>
              <line x1="12" y1="16" x2="12.01" y2="16"></line>
            </svg>
            {{ errorMessage }}
          </div>
        </transition>
      </div>
    </div>
  </div>
</template>

<script>
import { db } from "../firebase";
import { doc, setDoc } from "firebase/firestore";

export default {
  name: "AddQuestion",
  data() {
    return {
      roundNumber: null,
      questionText: "",
      deadline: "",
      successMessage: "",
      errorMessage: "",
      loading: false,
    };
  },
  methods: {
    formatDate(date) {
      if (!date) return "";
      const formattedDate = new Date(date);
      return formattedDate.toLocaleString("cs-CZ", {
        day: "numeric",
        month: "numeric",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    },

    async submitQuestion() {
      this.successMessage = "";
      this.errorMessage = "";

      if (!this.roundNumber || !this.questionText || !this.deadline) {
        this.errorMessage = "Prosím vyplňte všechna pole.";
        return;
      }

      this.loading = true;

      try {
        // Převedeme deadline na ISO string pro databázi
        const formattedDeadline = new Date(this.deadline).toISOString();

        // Uložíme do kolekce 'questions', ID dokumentu bude číslo kola (pro snadné dohledání)
        await setDoc(doc(db, "questions", String(this.roundNumber)), {
          roundNumber: this.roundNumber,
          question: this.questionText,
          evaluated: false,
          deadline: formattedDeadline,
          correctAnswer: null, // Placeholder pro budoucí vyhodnocení
        });

        this.successMessage = `Otázka pro ${
          this.roundNumber
        }. kolo byla uložena! Uzávěrka: ${this.formatDate(formattedDeadline)}`;

        // Reset formuláře
        this.roundNumber = null;
        this.questionText = "";
        this.deadline = "";
      } catch (error) {
        console.error("❌ Chyba při ukládání otázky:", error);
        this.errorMessage = "Chyba při ukládání: " + error.message;
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style scoped>
/* =========================================
   DARK THEME STYLING
   ========================================= */

.dark-theme-wrapper {
  background-color: #000000;
  min-height: 100vh;
  color: #ffffff;
  font-family: "Roboto", sans-serif;
}

.main-title {
  font-size: 2.5rem;
  font-weight: 800;
  letter-spacing: 2px;
  color: #fff;
  text-transform: uppercase;
}

.section-title {
  font-size: 1.5rem;
  font-weight: 700;
  color: #ccc;
  letter-spacing: 1px;
}

/* Karta */
.admin-card {
  background: #111;
  border: 1px solid #333;
  border-radius: 15px;
  padding: 2.5rem;
  max-width: 700px;
  box-shadow: 0 0 30px rgba(0, 0, 0, 0.5);
}

/* Inputy */
.form-label {
  font-size: 0.8rem;
  font-weight: bold;
  color: #888;
  letter-spacing: 1px;
  margin-bottom: 8px;
  display: block;
}

/* Input Group pro ikony */
.input-group {
  position: relative;
  display: flex;
  align-items: center;
}

.input-icon-wrapper {
  position: absolute;
  left: 15px;
  color: #666;
  z-index: 2;
  display: flex;
  align-items: center;
}

.dark-input {
  background-color: #000;
  border: 1px solid #444;
  color: #fff;
  padding: 12px 12px 12px 45px; /* Padding vlevo pro ikonu */
  border-radius: 8px;
  width: 100%;
  transition: all 0.3s;
}

.dark-input:focus {
  border-color: #dc3545;
  outline: none;
  box-shadow: 0 0 0 3px rgba(220, 53, 69, 0.2);
}

/* Fix pro kalendář v inputu */
input[type="datetime-local"] {
  color-scheme: dark;
}

/* Tlačítko */
.btn-red-glow {
  background: linear-gradient(135deg, #a71d2a 0%, #dc3545 100%);
  color: white;
  padding: 15px;
  border: none;
  border-radius: 8px;
  font-weight: bold;
  text-transform: uppercase;
  letter-spacing: 1px;
  transition: all 0.3s ease;
  box-shadow: 0 4px 15px rgba(220, 53, 69, 0.3);
  font-size: 1.1rem;
}

.btn-red-glow:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(220, 53, 69, 0.5);
}

.btn-red-glow:disabled {
  opacity: 0.7;
  cursor: not-allowed;
  transform: none;
}

/* Ikony */
.icon-sm {
  width: 20px;
  height: 20px;
}

/* Alerty */
.alert-box {
  padding: 15px;
  border-radius: 8px;
  text-align: center;
  font-weight: bold;
  display: flex;
  align-items: center;
  justify-content: center;
}

.alert-box.success {
  background: rgba(40, 167, 69, 0.1);
  color: #28a745;
  border: 1px solid #28a745;
}

.alert-box.error {
  background: rgba(220, 53, 69, 0.1);
  color: #ff6b6b;
  border: 1px solid #ff6b6b;
}

/* Animace */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.5s;
}
.fade-enter,
.fade-leave-to {
  opacity: 0;
}
</style>
